import { useState } from 'react';
import { useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/auth-context';
import { authService } from '@/services/api/auth.service';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/ui/form-field';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Lock, ArrowLeft, CheckCircle, Eye, EyeOff } from 'lucide-react';

type Step = 'form' | 'success';

export default function ChangePasswordPage() {
  const { t } = useTranslation('profile');
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  // Admin/staff return to their settings Profile tab; customers to home.
  const accountHref =
    user?.role === 'admin'
      ? '/admin/settings?tab=myProfile'
      : user?.role === 'staff'
        ? '/staff/settings?tab=myProfile'
        : '/';

  const [step, setStep] = useState<Step>('form');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError(t('changePassword.errorMismatch'));
      return;
    }

    // Validate minimum length
    if (newPassword.length < 8) {
      setError(t('changePassword.errorMinLength'));
      return;
    }

    setIsSubmitting(true);
    try {
      await authService.changePassword(currentPassword, newPassword);
      setStep('success');
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError(t('changePassword.errorIncorrect'));
      } else {
        setError(t('changePassword.errorFailed'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="container max-w-2xl mx-auto py-8 px-4">
      <Button
        variant="ghost"
        className="mb-6"
        onClick={() => setLocation(accountHref)}
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        {t('changePassword.backToProfile')}
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            {t('changePassword.title')}
          </CardTitle>
          <CardDescription>
            {step === 'form' && t('changePassword.description')}
            {step === 'success' && t('changePassword.successDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Password Form */}
          {step === 'form' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <FormField label={t('changePassword.currentPassword')}>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-soft" />
                  <Input
                    id="currentPassword"
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="pl-10 pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-3 text-soft hover:text-ink"
                    tabIndex={-1}
                  >
                    {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </FormField>

              <FormField
                label={t('changePassword.newPassword')}
                helperText={t('changePassword.minLengthHint')}
              >
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-soft" />
                  <Input
                    id="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pl-10 pr-10"
                    minLength={8}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-3 text-soft hover:text-ink"
                    tabIndex={-1}
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </FormField>

              <FormField label={t('changePassword.confirmPassword')}>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-soft" />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 pr-10"
                    minLength={8}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-3 text-soft hover:text-ink"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </FormField>

              <Button
                type="submit"
                className="w-full"
                disabled={isSubmitting || !currentPassword || !newPassword || !confirmPassword}
              >
                {isSubmitting ? t('changePassword.changing') : t('changePassword.changeButton')}
              </Button>
            </form>
          )}

          {/* Success */}
          {step === 'success' && (
            <div className="text-center space-y-6 py-6">
              <div className="flex justify-center">
                <div className="rounded-full bg-green-100 p-3">
                  <CheckCircle className="w-12 h-12 text-green-600" />
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="text-h1 text-ink">{t('changePassword.successTitle')}</h3>
                <p className="text-soft">
                  {t('changePassword.successMessage')}
                </p>
              </div>

              <Button
                onClick={() => setLocation(accountHref)}
                className="w-full"
              >
                {t('changePassword.returnToProfile')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
